/**
 * Create Safe Transaction Builder JSON
 */
import type { SafeTransaction, SafeTransactionBatch } from "../../types"
import { BASE_CHAIN_ID } from "../../types"

export interface ToSafeTransactionBatchParams {
	/** Name for the batch (shown in Safe UI) */
	name: string
	/** Optional description */
	description?: string
	/** Transactions to include */
	transactions: SafeTransaction[]
	/** Chain ID (default: Base mainnet 8453) */
	chainId?: number
}

/**
 * Create a Safe Transaction Builder JSON batch
 *
 * The output can be copy-pasted into the Safe Transaction Builder UI
 * or used with the Safe SDK.
 *
 * @example
 * ```ts
 * import { encodeResolve, encodeFinalize, toSafeTransactionBatch } from "@breakout/sdk/actions/encode"
 *
 * const batch = toSafeTransactionBatch({
 *   name: "Resolve Week 12 Markets",
 *   transactions: [
 *     encodeResolve({ market: "0xa...", winningBucket: 0, ... }),
 *     encodeResolve({ market: "0xb...", winningBucket: 2, ... }),
 *     encodeFinalize({ market: "0xc..." }),
 *   ],
 * })
 *
 * // Copy-paste JSON into Safe Transaction Builder
 * console.log(JSON.stringify(batch, null, 2))
 * ```
 */
export function toSafeTransactionBatch(params: ToSafeTransactionBatchParams): SafeTransactionBatch {
	const { name, description, transactions, chainId = BASE_CHAIN_ID } = params

	return {
		version: "1.0",
		chainId: chainId.toString(),
		meta: {
			name,
			...(description && { description }),
		},
		transactions,
	}
}

/**
 * Serialize a Safe batch to JSON string
 *
 * @example
 * ```ts
 * const json = serializeSafeBatch(batch)
 * // Ready to copy-paste into Safe UI
 * ```
 */
export function serializeSafeBatch(batch: SafeTransactionBatch): string {
	return JSON.stringify(batch, null, 2)
}
